import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mic, MicOff, SendHorizontal, Sparkles } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { ErrorMessage } from "@/components/error-message";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { useTenant } from "@/features/tenant/context";
import { type MessageKey, useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useSpeech } from "./speech";
import { aiStatusKey, useAiStatus } from "./use-ai";

interface Message {
  role: "user" | "assistant";
  text: string;
}

const SUGGESTIONS: MessageKey[] = [
  "ai.suggest.today",
  "ai.suggest.week",
  "ai.suggest.dues",
  "ai.suggest.runningOut",
  "ai.suggest.bestSellers",
];

const STORE = "zeeta.askZeeta";

function readHistory(tenantId: string): Message[] {
  try {
    return JSON.parse(sessionStorage.getItem(`${STORE}.${tenantId}`) ?? "[]") as Message[];
  } catch {
    return [];
  }
}

/** The chat behind the header's Ask Zeeta button: questions answered from the shop's reports. */
function AskPanel() {
  const { tenantId, branch } = useTenant();
  const { t, locale, num } = useI18n();
  const queryClient = useQueryClient();
  const status = useAiStatus(tenantId);
  const [messages, setMessages] = useState<Message[]>(() => readHistory(tenantId));
  const [draft, setDraft] = useState("");
  const end = useRef<HTMLDivElement>(null);
  const speech = useSpeech(locale, setDraft);

  useEffect(() => {
    try {
      sessionStorage.setItem(`${STORE}.${tenantId}`, JSON.stringify(messages.slice(-20)));
    } catch {
      // Private mode: the conversation is simply not kept.
    }
    end.current?.scrollIntoView({ block: "end" });
  }, [messages, tenantId]);

  const send = useMutation({
    mutationFn: (question: string) =>
      api.post<{ answer: string; remaining: number }>(`/t/${tenantId}/ai/ask`, {
        question,
        history: messages.slice(-6),
        branchId: branch.id,
        locale,
      }),
    onSuccess: (res) => {
      setMessages((m) => [...m, { role: "assistant", text: res.answer }]);
      queryClient.setQueryData(aiStatusKey(tenantId), (s: unknown) =>
        s ? { ...(s as object), remaining: res.remaining } : s,
      );
    },
    onError: () => setMessages((m) => m.slice(0, -1)),
  });

  const submit = (question: string) => {
    const q = question.trim();
    if (q.length < 2 || send.isPending) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setDraft("");
    send.mutate(q);
  };

  const s = status.data;
  if (s && !s.available)
    return (
      <p className="text-sm text-muted-foreground">
        {s.reason === "not_in_plan" ? t("ai.notInPlan") : t("ai.unavailable")}
      </p>
    );

  return (
    <div className="flex h-[min(70dvh,560px)] flex-col gap-3">
      <div className="flex-1 space-y-3 overflow-y-auto pr-1" aria-live="polite">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("ai.intro")}</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => submit(t(k))}
                  className="rounded-full border bg-card px-3 py-1.5 text-left text-sm hover:bg-muted"
                >
                  {t(k)}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: messages are append-only
            key={i}
            className={cn(
              "max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap",
              m.role === "user"
                ? "ml-auto rounded-br-sm bg-primary text-primary-foreground"
                : "rounded-bl-sm bg-muted",
            )}
          >
            {m.text}
          </div>
        ))}
        {send.isPending && (
          <div className="w-fit rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2 text-sm text-muted-foreground">
            {t("ai.thinking")}
          </div>
        )}
        <div ref={end} />
      </div>
      <ErrorMessage error={send.error} />
      <form
        className="flex items-center gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          submit(draft);
        }}
      >
        <input
          aria-label={t("ai.question")}
          placeholder={speech.listening ? t("ai.listening") : t("ai.placeholder")}
          value={draft}
          maxLength={500}
          onChange={(e) => setDraft(e.target.value)}
          className="h-11 min-w-0 flex-1 rounded-full border border-input bg-card px-4 text-sm focus-visible:outline-2 focus-visible:outline-ring"
        />
        {speech.supported && (
          <Button
            variant={speech.listening ? "destructive" : "outline"}
            size="icon"
            className="rounded-full"
            aria-label={speech.listening ? t("ai.stopListening") : t("ai.speak")}
            onClick={() => (speech.listening ? speech.stop() : speech.start())}
          >
            {speech.listening ? <MicOff aria-hidden /> : <Mic aria-hidden />}
          </Button>
        )}
        <Button
          type="submit"
          size="icon"
          className="rounded-full"
          aria-label={t("ai.send")}
          loading={send.isPending}
          disabled={draft.trim().length < 2}
        >
          <SendHorizontal aria-hidden />
        </Button>
      </form>
      <p className="text-[11px] text-muted-foreground">
        {s && t("ai.remaining", { n: num(s.remaining), quota: num(s.quota) })} {t("ai.privacy")}
      </p>
    </div>
  );
}

export function AskZeetaButton() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="gap-1.5">
        <Sparkles aria-hidden />
        <span className="hidden sm:inline">{t("ai.ask")}</span>
        <span className="sr-only sm:hidden">{t("ai.ask")}</span>
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t("ai.ask")} size="lg">
        <AskPanel />
      </Dialog>
    </>
  );
}
